import React, { useDeferredValue, useMemo, useState } from 'react';
import cx from 'classnames';
import { PushPin, PushPinOutlined, Search } from '@mui/icons-material';
import { CircularProgress, Skeleton, Tooltip } from '@mui/material';
import { TextField } from 'new/elements/TextField';
import { ReactComponent as DatabaseIcon } from 'new/components/icons/fi-rr-database.svg';
import { ReactComponent as TableIcon } from 'new/components/icons/fi-rr-table.svg';
import { ReactComponent as ChevronIcon } from 'new/components/icons/chevron_right.svg';

export type SchemaColumn = {
  name: string;
  type?: string;
  primaryKey?: boolean;
  foreignKey?: boolean;
  nullable?: boolean;
  defaultValue?: unknown;
};

export type SchemaTable = {
  id: string;
  name: string;
  database?: string;
  columns?: SchemaColumn[];
  scanned?: boolean;
};

export type SchemaDatabase = {
  id: string;
  name: string;
};

type Props = {
  databases: SchemaDatabase[];
  tablesForDatabase: Record<string, string[]>;
  tables: Record<string, SchemaTable>;
  selectedDatabaseId?: string | null;
  selectedTableId?: string | null;
  pinnedTableIds: string[];
  onSelectDatabase: (databaseId: string) => void;
  onSelectTable: (tableId: string) => void;
  onTogglePin: (tableId: string) => void;
  onProbeTable: (tableId: string) => void;
  /** Tree is still fetching the database list (initial connect / probe). */
  loading?: boolean;
  /** Connector display name, for the "Connecting to …" message. */
  connectionName?: string;
};

const COLUMN_PEEK_LIMIT = 16;

// Transparent tooltip container — the peek card supplies its own chrome. The
// negative offset pulls the card left so it straddles the tree pane edge
// rather than floating entirely outside it.
const PEEK_TOOLTIP_PROPS = {
  tooltip: {
    sx: {
      bgcolor: 'transparent',
      p: 0,
      m: 0,
      maxWidth: 'none',
    },
  },
  popper: {
    // Overlap the tree's right edge so the card reads as connected to the row.
    // The pin now lives on the row's left, so this no longer covers it.
    sx: { zIndex: 1400 },
    modifiers: [{ name: 'offset', options: { offset: [0, -110] } }],
  },
};

const ColumnPeek: React.FC<{ table: SchemaTable; loading: boolean }> = ({ table, loading }) => {
  const columns = table.columns ?? [];
  const shown = columns.slice(0, COLUMN_PEEK_LIMIT);
  const hidden = columns.length - shown.length;

  return (
    <div className="db-tree-peek">
      <div className="db-tree-peek-head">
        <span className="db-tree-peek-title">{table.name}</span>
        {columns.length > 0 && (
          <span className="db-tree-peek-count">
            {columns.length} {columns.length === 1 ? 'column' : 'columns'}
          </span>
        )}
      </div>

      {columns.length === 0 ? (
        <div className="db-tree-peek-status">
          {loading ? (
            <>
              <CircularProgress size={13} thickness={5} />
              <span>Loading columns…</span>
            </>
          ) : (
            <span>No columns available.</span>
          )}
        </div>
      ) : (
        <>
          <ul className="db-tree-peek-list">
            {shown.map((c) => (
              <li key={c.name}>
                <span className="db-tree-peek-col">
                  {c.name}
                  {c.primaryKey ? <span className="db-tree-peek-pk">PK</span> : null}
                </span>
                {c.type ? <span className="db-tree-peek-type">{c.type}</span> : null}
              </li>
            ))}
          </ul>
          {hidden > 0 ? <div className="db-tree-peek-more">+{hidden} more</div> : null}
        </>
      )}
    </div>
  );
};

const TableRow: React.FC<{
  table: SchemaTable;
  active: boolean;
  pinned: boolean;
  onSelect: () => void;
  onTogglePin: () => void;
  onProbe: () => void;
}> = ({ table, active, pinned, onSelect, onTogglePin, onProbe }) => {
  // Columns are loaded lazily; a table is "scanned" once its schema arrives.
  const loading = !table.scanned;
  const row = (
    <div
      role="button"
      tabIndex={0}
      className={cx('db-tree-table', { 'is-active': active })}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      data-testid={`db-tree-table-${table.name}`}
    >
      <button
        type="button"
        className={cx('db-tree-table-pin', { 'is-pinned': pinned })}
        aria-label={pinned ? 'Unpin table' : 'Pin table'}
        onClick={(e) => {
          e.stopPropagation();
          onTogglePin();
        }}
      >
        {pinned ? <PushPin fontSize="inherit" /> : <PushPinOutlined fontSize="inherit" />}
      </button>
      <TableIcon className="db-tree-table-icon" />
      <span className="db-tree-table-name">{table.name}</span>
    </div>
  );

  return (
    <Tooltip
      placement="right-start"
      enterDelay={350}
      // Grace period so the cursor can travel into the (interactive) card to
      // scroll the column list; the card stays open while hovered.
      leaveDelay={250}
      componentsProps={PEEK_TOOLTIP_PROPS}
      onOpen={() => {
        if (loading) onProbe();
      }}
      title={<ColumnPeek table={table} loading={loading} />}
    >
      {row}
    </Tooltip>
  );
};

export const SchemaTreePanel: React.FC<Props> = ({
  databases,
  tablesForDatabase,
  tables,
  selectedDatabaseId,
  selectedTableId,
  pinnedTableIds,
  onSelectDatabase,
  onSelectTable,
  onTogglePin,
  onProbeTable,
  loading,
  connectionName,
}) => {
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);

  const allTables = useMemo(() => Object.values(tables), [tables]);

  const pinnedSet = useMemo(() => new Set(pinnedTableIds), [pinnedTableIds]);

  const filtered = useMemo(() => {
    const q = deferredQuery.trim().toLowerCase();
    if (!q) return null;
    return allTables.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.database ?? '').toLowerCase().includes(q) ||
        (t.columns ?? []).some((c) => c.name.toLowerCase().includes(q)),
    );
  }, [deferredQuery, allTables]);

  const pinnedEntries = useMemo(
    () => allTables.filter((t) => pinnedSet.has(t.id)),
    [allTables, pinnedSet],
  );

  const renderTableRow = (table: SchemaTable, keyPrefix = '') => (
    <TableRow
      key={`${keyPrefix}${table.id}`}
      table={table}
      active={table.id === selectedTableId}
      pinned={pinnedSet.has(table.id)}
      onSelect={() => onSelectTable(table.id)}
      onTogglePin={() => onTogglePin(table.id)}
      onProbe={() => onProbeTable(table.id)}
    />
  );

  return (
    <div className="db-tree-panel">
      <div className="db-tree-search">
        <TextField
          startAdornment={<Search />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search tables, columns…"
          inputProps={{ 'aria-label': 'Search tables and columns' }}
          fullWidth
        />
      </div>

      <div className="db-tree-body">
        {filtered ? (
          filtered.length === 0 ? (
            <div className="db-tree-no-match">No matches</div>
          ) : (
            filtered.map((t) => renderTableRow(t, 'search-'))
          )
        ) : (
          <>
            {pinnedEntries.length > 0 && (
              <div className="db-tree-section">
                <div className="db-tree-section-label">
                  <PushPin fontSize="inherit" /> Recent
                </div>
                {pinnedEntries.map((t) => renderTableRow(t, 'pinned-'))}
              </div>
            )}

            {databases.length === 0 ? (
              loading ? (
                <div className="db-tree-status">
                  <CircularProgress size={14} thickness={5} />
                  <span>Connecting{connectionName ? ` to ${connectionName}` : ''}…</span>
                </div>
              ) : (
                <div className="db-tree-empty">No databases found.</div>
              )
            ) : (
              databases.map((db) => {
                const tableIds = tablesForDatabase[db.id] ?? [];
                const open =
                  db.id === selectedDatabaseId ||
                  (!!selectedTableId && tableIds.includes(selectedTableId));
                return (
                  <div key={db.id} className="db-tree-db">
                    <button
                      type="button"
                      className="db-tree-db-row"
                      onClick={() => onSelectDatabase(db.id)}
                      data-testid={`db-tree-db-${db.name}`}
                    >
                      <ChevronIcon className={cx('db-tree-chevron', { 'is-open': open })} />
                      <DatabaseIcon className="db-tree-db-icon" />
                      <span className="db-tree-db-name">{db.name}</span>
                      {tableIds.length > 0 && (
                        <span className="db-tree-db-count">{tableIds.length}</span>
                      )}
                    </button>
                    {open && tableIds.length > 0 && (
                      <div className="db-tree-db-tables">
                        {tableIds
                          .map((id) => tables[id])
                          .filter(Boolean)
                          .map((t) => renderTableRow(t))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </>
        )}
      </div>

      <div className="db-tree-footer">
        {loading ? <Skeleton variant="text" width={48} /> : `${allTables.length} tables`}
        {' · click to preview'}
      </div>
    </div>
  );
};
